import React, { useState, useEffect } from 'react';
import NavigationBar from './components/NavigationBar';
import Footer from './components/Footer';
import RegistrationForm from './components/RegistrationForm';
import AdminPanel from './components/AdminPanel';
import LoginPage from './components/LoginPage';
import WhatsAppButton from './components/WhatsAppButton';
import JobApplicationForm from './components/JobApplicationForm';
import HomePage from './pages/HomePage';
import ProgramPage from './pages/ProgramPage';
import SilabusPage from './pages/SilabusPage';
import SchedulePage from './pages/SchedulePage';
import AboutPage from './pages/AboutPage';
import JobVacancyPage from './pages/JobVacancyPage';
import { registrationAPI } from './services/api';

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [currentPortfolio, setCurrentPortfolio] = useState(0);
  const [currentMentor, setCurrentMentor] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState('all');

  // Registration state
  const [registrations, setRegistrations] = useState([]);
  const [isRegFormOpen, setIsRegFormOpen] = useState(false);
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);

  // Multi-select programs state
  const [selectedPrograms, setSelectedPrograms] = useState([]);

  // Job application state
  const [isJobFormOpen, setIsJobFormOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginPage, setShowLoginPage] = useState(false);

  // Load registrations from backend on mount
  useEffect(() => {
    const loadRegistrations = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const data = await registrationAPI.getAll();
          setRegistrations(data);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Failed to load registrations:', error);
          // Token might be expired, clear it
          localStorage.removeItem('authToken');
        }
      }
    };
    loadRegistrations();
  }, []);

  // Reload registrations when admin panel opens
  useEffect(() => {
    if (isAdminPanelOpen && isAuthenticated) {
      const loadRegistrations = async () => {
        try {
          const data = await registrationAPI.getAll();
          setRegistrations(data);
        } catch (error) {
          console.error('Failed to reload registrations:', error);
        }
      };
      loadRegistrations();
    }
  }, [isAdminPanelOpen, isAuthenticated]);

  const handleRegister = (program) => {
    setSelectedProgram(program);
    setIsRegFormOpen(true);
  };

  const handleSubmitRegistration = async (registration) => {
    try {
      await registrationAPI.create(registration);
      // Success message now shown via RegistrationForm popup
    } catch (error) {
      console.error('Failed to submit registration:', error);
      alert('Gagal mengirim pendaftaran. Silakan coba lagi.');
    }
  };

  const handleVerifyRegistration = async (id) => {
    try {
      await registrationAPI.updateStatus(id, 'verified');
      // Update local state
      setRegistrations(prev =>
        prev.map(reg =>
          reg.id === id ? { ...reg, status: 'verified' } : reg
        )
      );
      alert('Pendaftaran berhasil diverifikasi!');
    } catch (error) {
      console.error('Failed to verify registration:', error);
      alert('Gagal memverifikasi pendaftaran.');
    }
  };

  const handleRejectRegistration = async (id) => {
    try {
      await registrationAPI.updateStatus(id, 'rejected');
      // Update local state
      setRegistrations(prev =>
        prev.map(reg =>
          reg.id === id ? { ...reg, status: 'rejected' } : reg
        )
      );
      alert('Pendaftaran ditolak.');
    } catch (error) {
      console.error('Failed to reject registration:', error);
      alert('Gagal menolak pendaftaran.');
    }
  };

  const handleAdminClick = () => {
    if (isAuthenticated) {
      setIsAdminPanelOpen(true);
    } else {
      setShowLoginPage(true);
    }
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
    setShowLoginPage(false);
    setIsAdminPanelOpen(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setIsAdminPanelOpen(false);
    localStorage.removeItem('authToken');
    setRegistrations([]);
  };

  const handleApplyJob = (job) => {
    setSelectedJob(job);
    setIsJobFormOpen(true);
  };

  const handleSubmitJobApplication = async (application) => {
    try {
      const response = await fetch('http://localhost:5001/api/job-applications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(application)
      });

      const data = await response.json();

      if (data.success) {
        alert('Lamaran berhasil dikirim! Kami akan menghubungi Anda segera.');
      } else {
        alert('Gagal mengirim lamaran. Silakan coba lagi.');
      }
    } catch (error) {
      console.error('Failed to submit job application:', error);
      alert('Gagal mengirim lamaran. Silakan coba lagi.');
    }
  };

  const renderPage = () => {
    switch(currentPage) {
      case 'home':
        return (
          <HomePage
            setCurrentPage={setCurrentPage}
            currentPortfolio={currentPortfolio}
            setCurrentPortfolio={setCurrentPortfolio}
            currentMentor={currentMentor}
            setCurrentMentor={setCurrentMentor}
            registrations={registrations}
          />
        );
      case 'program':
        return (
          <ProgramPage
            selectedTrack={selectedTrack}
            setSelectedTrack={setSelectedTrack}
            onRegister={handleRegister}
            selectedPrograms={selectedPrograms}
            setSelectedPrograms={setSelectedPrograms}
          />
        );
      case 'silabus':
        return <SilabusPage />;
      case 'schedule':
        return <SchedulePage setCurrentPage={setCurrentPage} />;
      case 'about':
        return <AboutPage />;
      case 'careers':
        return <JobVacancyPage onApply={handleApplyJob} />;
      default:
        return (
          <HomePage
            setCurrentPage={setCurrentPage}
            currentPortfolio={currentPortfolio}
            setCurrentPortfolio={setCurrentPortfolio}
            currentMentor={currentMentor}
            setCurrentMentor={setCurrentMentor}
            registrations={registrations}
          />
        );
    }
  };

  // Show login page if requested
  if (showLoginPage) {
    return (
      <LoginPage
        onLogin={handleLogin}
        onClose={() => setShowLoginPage(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white transition-colors">
      <NavigationBar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        onAdminClick={handleAdminClick}
      />
      {renderPage()}
      <Footer setCurrentPage={setCurrentPage} />

      {/* Registration Form Modal */}
      <RegistrationForm
        isOpen={isRegFormOpen}
        onClose={() => setIsRegFormOpen(false)}
        selectedProgram={selectedProgram}
        onSubmit={handleSubmitRegistration}
      />

      {/* Admin Panel Modal */}
      <AdminPanel
        isOpen={isAdminPanelOpen}
        onClose={() => setIsAdminPanelOpen(false)}
        registrations={registrations}
        onVerify={handleVerifyRegistration}
        onReject={handleRejectRegistration}
        onLogout={handleLogout}
        isAuthenticated={isAuthenticated}
      />

      {/* Job Application Form Modal */}
      <JobApplicationForm
        isOpen={isJobFormOpen}
        onClose={() => setIsJobFormOpen(false)}
        selectedJob={selectedJob}
        onSubmit={handleSubmitJobApplication}
      />

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />
    </div>
  );
};

export default App;
